import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/stores/auth';

/** Classes (with the subjects this person may teach in each) and CBT tests to link, for teachers and for owner/desk. */
export interface ClassOpt { id: number; name: string; subjects: { id: number; name: string }[] }

export function useTeachingOptions() {
  const role = useAuth((s) => s.user?.role);
  const admin = role === 'owner' || role === 'desk';
  const classes = useQuery({
    queryKey: ['teach-classes', admin],
    queryFn: async (): Promise<ClassOpt[]> => {
      if (!admin) {
        const r = await api.get('/teacher/classes');
        return (r.data?.data ?? []).map((c: any) => ({ id: c.class_id, name: c.class, subjects: c.subjects ?? [] }));
      }
      const [c, s] = await Promise.all([api.get('/school/classes'), api.get('/school/subjects', { params: { active_only: 1 } })]);
      const subjects = (s.data?.data ?? []).map((x: any) => ({ id: x.id, name: x.name }));
      return (c.data?.data ?? []).filter((x: any) => x.is_active !== false).map((x: any) => ({ id: x.id, name: `${x.name}${x.arm ? ' ' + x.arm : ''}`, subjects }));
    },
  });
  const quizzes = useQuery({
    queryKey: ['teach-quizzes', admin],
    queryFn: () => api.get(admin ? '/school/cbt-exams' : '/teacher/cbt-exams', { params: { per_page: 100 } }).then((r) => (r.data?.data ?? []) as { id: number; title: string; status: string }[]),
  });
  return { admin, role, classes: classes.data ?? [], quizzes: quizzes.data ?? [], loading: classes.isLoading };
}

/** Upload one file to /media/upload and get its public URL back. */
export async function uploadFile(file: File): Promise<{ url: string; name: string; mime: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return { url: r.data?.data?.url, name: file.name, mime: r.data?.data?.mime };
}

export const fmtWhen = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
